//@flow
import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {
  Divider,
  Grid,
  IconButton,
  makeStyles,
  Typography,
} from '@material-ui/core';
import CustomSelect from '../../../../../../elements/CustomSelect/CustomSelect';
import CustomButton from '../../../../../../elements/CustomButton/CustomButton';
import ModalSelectItems from '../../../../../../elements/ModalSelectItems/ModalSelectItems';
import {getImageUrl} from '../../../../../../utils/image_provider';
import {
  useSubCategories,
  useSubCategoriesActions,
} from '../../../../../sub-categories/utils';
import {
  useCategoriesActions,
  useCategoriesFilter,
} from '../../../../../categories/utils';
import {
  useManualOrder,
  useManualOrderActions,
} from '../../../../../manual-order/utils';
import {colors} from '../../../../../../assets/styles/colors';
import {DeleteOutlined} from '@material-ui/icons';
import {useRelatedProducts} from '../../../../../products/utils/hooks';
import {useProductContext} from '../../../../context';
import {useEditProduct} from '../../../../utils/editProduct';

import type {RelatedProducts} from '../../../../../products/api';

const useStyles = makeStyles(theme => ({
  modalRoot: {
    display: 'flex',
    flexDirection: 'column',
    justifyItems: 'center',
    alignItems: 'center',
    textAlign: 'center',
    maxWidth: 600,
    width: '90vw',
    gap: '1.5rem',
    overflow: 'hidden',
  },
  content: {
    width: '100%',
    height: '100%',
    maxHeight: 'calc(100vh - 300px)',
    overflow: 'hidden auto',
  },
  title: {
    fontWeight: 600,
    fontSize: '1.375rem',
    color: colors.grey800,
  },
  submitButton: {
    maxWidth: '240px',
    fontSize: '1.125rem',
    fontWeight: 600,
  },

  contentSection: {
    display: 'flex',
    flexDirection: 'column',
    marginTop: theme.spacing(2),
    borderRadius: '12px',
    boxShadow:
      '0px 0px 1px rgba(40, 41, 61, 0.08), 0px 0.5px 2px rgba(96, 97, 112, 0.16)',
    padding: theme.spacing(3),
    [theme.breakpoints.down('xs')]: {
      padding: theme.spacing(2),
    },
    background: 'white',
  },
  sectionTitle: {
    fontSize: '1rem',
    display: 'flex',
    alignItems: 'center',
    fontWeight: 600,
  },
  sectionDescription: {
    color: colors.grey700,
    fontSize: '0.875rem',
  },
  sectionSubtitle: {
    color: colors.grey900,
    fontSize: 14,
    fontWeight: 500,
  },
  divider: {
    margin: theme.spacing(2, 0),
  },
  sectionCheckbox: {
    '& .MuiFormControlLabel-label': {
      color: colors.grey700,
      fontSize: '0.875rem',
    },
  },
  infoText: {
    color: colors.grey900,
    fontSize: 14,
  },
  spaceBetween: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  selectProducts: {
    height: 48,
    width: '100%',
    borderRadius: 8,
    '& span': {
      borderRadius: 8,
    },
  },
  wrapSelect: {
    width: '100%',
    margin: theme.spacing(1, 0),
  },
  contentList: {
    borderRadius: '12px',
    marginTop: theme.spacing(1),
    background: colors.grey100,
  },
  listItemsWrapper: {
    overflow: 'auto',
    padding: '16px',
    maxHeight: '530px',
  },
  deleteItems: {
    color: '#E13065',
    fontWeight: 600,
    fontSize: '12px',
    lineHeight: '16px',
  },
  item: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  rowItem: {
    width: '100%',
    display: 'flex',
  },
  imageItem: {
    borderRadius: '8px',
  },
  wrapItemName: {
    width: '100%',
    margin: theme.spacing(0, 1, 0, 2),
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
  },
  nameItem: {
    width: '100%',
    textAlign: 'left',
    fontWeight: 400,
    fontSize: '16px',
    lineHeight: '24px',
    color: colors.grey800,
  },
  deleteButton: {
    color: '#E54D7B',
  },
  wrapperItem: {
    width: '100%',
  },
}));

type RelatedProductsOrigin =
  | 'PRODUCT'
  | 'PRODUCT_CATEGORY'
  | 'PRODUCT_SUB_CATEGORY';

type Props = {
  onSubmit: () => void,
};

export default function SimilarProductsModal(props: Props) {
  const classes = useStyles();

  const [productId] = useProductContext(product => product.id);
  const {editProduct} = useEditProduct(productId);

  const {
    isFetchingSubcategories,
    paginationSubcategories,
    subcategoriesFilter,
  } = useSubCategories();
  const {
    fetchSubCategories,
    resetSubCategoriesFilter,
  } = useSubCategoriesActions();

  const {
    categoriesFilter,
    isFetchingCategories,
    paginationCategories,
  } = useCategoriesFilter();
  const {fetchCategoriesSearch, resetCategoriesFilter} = useCategoriesActions();

  const {
    isFetchingProducts,
    paginationProducts,
    products: productsFilter,
  } = useManualOrder();
  const {resetProductsFilter, fetchProducts} = useManualOrderActions();

  const [relatedProductModal, setRelatedProductModal] = useState(false);

  const [
    relatedProductsOrigin,
    setRelatedProductsOrigin,
  ] = useState<RelatedProductsOrigin>('PRODUCT');

  const {fetchRelatedProducts, relatedProducts} = useRelatedProducts();

  const modalTitle = {
    PRODUCT: 'Agregar productos similares',
    PRODUCT_CATEGORY: 'Seleccionar categorías',
    PRODUCT_SUB_CATEGORY: 'Seleccionar subcategorías',
  }[relatedProductsOrigin];

  const itemsSelected = {
    PRODUCT: productsFilter,
    PRODUCT_CATEGORY: categoriesFilter,
    PRODUCT_SUB_CATEGORY: subcategoriesFilter,
  }[relatedProductsOrigin];

  const paginationSelected = {
    PRODUCT: paginationProducts,
    PRODUCT_CATEGORY: paginationCategories,
    PRODUCT_SUB_CATEGORY: paginationSubcategories,
  }[relatedProductsOrigin];

  const resetListFilterSelected = {
    PRODUCT: resetProductsFilter,
    PRODUCT_CATEGORY: resetCategoriesFilter,
    PRODUCT_SUB_CATEGORY: resetSubCategoriesFilter,
  }[relatedProductsOrigin];

  const [listItems, setListItems] = useState([]);
  const [listProductsItems, setListProductsItems] = useState([]);
  const [listCategoryItems, setListCategoryItems] = useState([]);
  const [listSubcategoriesItems, setListSubcategoriesItems] = useState([]);

  const listCustomItems = useMemo(() => {
    return {
      PRODUCT: listProductsItems,
      PRODUCT_CATEGORY: listCategoryItems,
      PRODUCT_SUB_CATEGORY: listSubcategoriesItems,
    };
  }, [listCategoryItems, listProductsItems, listSubcategoriesItems]);

  const setterCustomItems = useMemo(() => {
    return {
      PRODUCT: setListProductsItems,
      PRODUCT_CATEGORY: setListCategoryItems,
      PRODUCT_SUB_CATEGORY: setListSubcategoriesItems,
    };
  }, [setListCategoryItems, setListProductsItems, setListSubcategoriesItems]);

  const isFetchingItemsSelected = {
    PRODUCT: isFetchingProducts,
    PRODUCT_CATEGORY: isFetchingCategories,
    PRODUCT_SUB_CATEGORY: isFetchingSubcategories,
  }[relatedProductsOrigin];

  const currentListItems = useMemo(() => {
    switch (relatedProductsOrigin) {
      case 'PRODUCT':
        return listCustomItems.PRODUCT;
      case 'PRODUCT_CATEGORY':
        return listCustomItems.PRODUCT_CATEGORY;
      case 'PRODUCT_SUB_CATEGORY':
        return listCustomItems.PRODUCT_SUB_CATEGORY;
      default:
        return [];
    }
  }, [listCustomItems, relatedProductsOrigin]);

  const currentSetItems = useMemo(() => {
    switch (relatedProductsOrigin) {
      case 'PRODUCT':
        return setterCustomItems.PRODUCT;
      case 'PRODUCT_CATEGORY':
        return setterCustomItems.PRODUCT_CATEGORY;
      case 'PRODUCT_SUB_CATEGORY':
        return setterCustomItems.PRODUCT_SUB_CATEGORY;
      default:
        return () => null;
    }
  }, [relatedProductsOrigin, setterCustomItems]);

  const handleSetListItems = useCallback(
    value => {
      setListItems(value);
      //$FlowFixMe
      currentSetItems(value);
    },

    [currentSetItems, setListItems]
  );

  useEffect(() => {
    if (!productId) return;

    fetchRelatedProducts(productId);
  }, [fetchRelatedProducts, productId]);

  useEffect(() => {
    if (!relatedProducts) return;
    const criteria = relatedProducts.relatedProductCriteria;
    // setEnabledRelatedProducts(relatedProducts.enabled);

    const cleanProductItems = relatedProducts.relatedProducts
      ? relatedProducts.relatedProducts.map(product => ({
          id: product.id,
          name: product.name,
          mainImage: {id: product.mainImage ? product.mainImage.id : ''},
        }))
      : [];
    setListProductsItems(cleanProductItems);
    const cleanCategoryItems = relatedProducts.relatedProductCategories
      ? relatedProducts.relatedProductCategories.map(category => ({
          id: category.id,
          name: category.name,
          mainImage: {id: category.portraitImageId},
        }))
      : [];
    setListCategoryItems(cleanCategoryItems);
    const cleanSubcategoryItems = relatedProducts.relatedProductSubCategories
      ? relatedProducts.relatedProductSubCategories.map(subcategory => ({
          id: subcategory.id,
          name: subcategory.name,
          mainImage: null,
        }))
      : [];
    setListSubcategoriesItems(cleanSubcategoryItems);

    switch (criteria) {
      case 'PRODUCT':
        setListItems(cleanProductItems);
        break;
      case 'PRODUCT_CATEGORY':
        setListItems(cleanCategoryItems);
        break;
      case 'PRODUCT_SUB_CATEGORY':
        setListItems(cleanSubcategoryItems);
        break;
      default:
        break;
    }

    setRelatedProductsOrigin(criteria);
  }, [relatedProducts]);

  const handleDeleteListItem = (id: string) => {
    const newList: Object[] = listItems.filter(el => el.id !== id);
    //$FlowFixMe
    currentSetItems(prev => prev.filter(el => el.id !== id));
    setListItems(newList);
  };

  const customFetchItemsCallback = useCallback(
    (queryFilter: {[key: string]: any}, addPage?: boolean = false) => {
      if (queryFilter) {
        if (relatedProductsOrigin === 'PRODUCT') {
          fetchProducts({queryFilter, addPage});
        } else if (relatedProductsOrigin === 'PRODUCT_CATEGORY') {
          fetchCategoriesSearch({queryFilter, addPage});
        } else {
          fetchSubCategories({queryFilter, addPage});
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [fetchCategoriesSearch, fetchProducts, relatedProductsOrigin]
  );

  const buildCustomQueryParamsCallback = useCallback(() => {
    let params = {};
    if (relatedProductsOrigin === 'PRODUCT') {
      params = {
        enabled: 1,
        size: 8,
      };
    } else if (relatedProductsOrigin === 'PRODUCT_CATEGORY') {
      params = {
        size: 8,
      };
    } else {
      params = {
        size: 8,
      };
    }
    return params;
  }, [relatedProductsOrigin]);

  const onSelectChange = e => {
    //$FlowFixMe
    currentSetItems(listItems);
    setListItems(listCustomItems[e.target.value] || []);
    setRelatedProductsOrigin(e.target.value);
  };

  const buildRowItem = (item: any) => {
    return (
      <div className={classes.item}>
        <div className={classes.rowItem}>
          <img
            className={classes.imageItem}
            alt={item.name}
            src={getImageUrl(
              item.mainImage
                ? item.mainImage.id
                : item.mainImageId
                ? item.mainImageId
                : item.portraitImageId
            )}
            width="56"
            height="56"
          />
          <div className={classes.wrapItemName}>
            <div className={classes.nameItem}>{item.name}</div>
          </div>
        </div>
        <div>
          <IconButton onClick={() => handleDeleteListItem(item.id)}>
            <DeleteOutlined className={classes.deleteButton} />
          </IconButton>
        </div>
      </div>
    );
  };

  const onSubmit = () => {
    const relatedProductSetting: RelatedProducts = {
      enabled: true,
      criteria: relatedProductsOrigin,
    };

    if (relatedProductsOrigin === 'PRODUCT') {
      relatedProductSetting.products = listProductsItems.map(({id}) => id);
    }
    if (relatedProductsOrigin === 'PRODUCT_CATEGORY') {
      relatedProductSetting.productCategories = listCategoryItems.map(
        ({id}) => id
      );
    }
    if (relatedProductsOrigin === 'PRODUCT_SUB_CATEGORY') {
      relatedProductSetting.productSubCategories = listSubcategoriesItems.map(
        ({id}) => id
      );
    }

    editProduct({
      relatedProductSetting,
    });

    props.onSubmit();
  };

  return (
    <>
      <aside className={classes.modalRoot}>
        <Typography className={classes.title} align="center">
          Productos similares
        </Typography>
        <div className={classes.content}>
          <Typography className={classes.sectionSubtitle}>
            Selecciona el origen de los productos similares
          </Typography>
          <div className={classes.wrapSelect}>
            <Grid container spacing={2}>
              <Grid item xs={6} sm={6}>
                <CustomSelect
                  classes={{root: classes.bigSelect}}
                  native
                  fullWidth
                  value={relatedProductsOrigin}
                  onChange={onSelectChange}
                  // disabled={disabled}
                >
                  <option value="" hidden />
                  <option value="PRODUCT">Productos específicos</option>
                  <option value="PRODUCT_CATEGORY">
                    Categorias específicas
                  </option>
                  <option value="PRODUCT_SUB_CATEGORY">
                    Subcategorias específicas
                  </option>
                </CustomSelect>
              </Grid>
              <Grid item xs={6} sm={6}>
                <CustomButton
                  fullWidth
                  variant="outlined"
                  className={classes.selectProducts}
                  onClick={() => setRelatedProductModal(true)}
                  disabled={currentListItems.length >= 5}
                >
                  Seleccionar{' '}
                  {
                    {
                      PRODUCT: 'Productos',
                      PRODUCT_CATEGORY: 'Categorías',
                      PRODUCT_SUB_CATEGORY: 'Subcategorías',
                    }[relatedProductsOrigin]
                  }
                </CustomButton>
              </Grid>
            </Grid>
          </div>
          <Typography
            className={classes.sectionSubtitle}
            style={{marginTop: 8}}
          >
            {
              {
                PRODUCT: 'Productos',
                PRODUCT_CATEGORY: 'Categorías',
                PRODUCT_SUB_CATEGORY: 'Subcategorías',
              }[relatedProductsOrigin]
            }{' '}
            seleccionados ({listItems.length}/5)
          </Typography>
          {listItems.length > 0 && (
            <div className={classes.contentList}>
              <Grid container spacing={1} className={classes.listItemsWrapper}>
                {listItems.map((p, index: number) => (
                  <Grid item xs={12} key={index} className={classes.item}>
                    <div className={classes.wrapperItem}>
                      {buildRowItem(p)}
                      {index < listItems.length - 1 && (
                        <Divider className={classes.divider} />
                      )}
                    </div>
                  </Grid>
                ))}
              </Grid>
            </div>
          )}
        </div>
        <CustomButton
          className={classes.submitButton}
          color="primary"
          variant="contained"
          fullWidth
          onClick={onSubmit}
        >
          Guardar
        </CustomButton>
      </aside>
      <ModalSelectItems
        maxItems={5}
        title={modalTitle}
        open={relatedProductModal}
        handleClose={() => setRelatedProductModal(false)}
        items={itemsSelected}
        type={relatedProductsOrigin}
        pagination={paginationSelected}
        isFetchingItems={isFetchingItemsSelected}
        setListItems={handleSetListItems}
        listItems={listItems}
        resetListFilter={resetListFilterSelected}
        customFetchItemsCallback={customFetchItemsCallback}
        buildCustomQueryParamsCallback={buildCustomQueryParamsCallback}
      />
    </>
  );
}
